import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import contactService from '@api/v1/contactService'
import matterService from '@api/v1/matterService'
import { requireArray } from '@api/v1/readShape'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'
import LoadingSpinner from '@components/common/LoadingSpinner'
import { toast } from '@store/toastStore'

const TABS = ['Overview', 'Matters', 'Activities']

const DetailRow = ({ label, value }) => (
  <div className="flex items-start py-2.5 border-b border-gray-50 last:border-0">
    <dt className="w-36 shrink-0 text-sm text-gray-500">{label}</dt>
    <dd className="text-sm text-gray-900">{value || '—'}</dd>
  </div>
)

const ContactDetail = () => {
  useDocumentTitle('Contact Detail')
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('Overview')
  const [editing, setEditing] = useState(false)
  const [editData, setEditData] = useState({})

  /*
   * CC-53132: three outcomes, not two — see `@api/v1/readState`.
   *
   * This page used to branch on `isLoading` and then on `error || !contact`.
   * `isLoading` is `isPending && isFetching`, so it is FALSE for a query that is
   * pending but NOT in flight — above all a PAUSED one (react-query's default
   * `networkMode: 'online'` while `onlineManager` believes the browser is
   * offline). `queryFn` is never called, nothing throws, nothing is logged, and
   * `data` stays `undefined` — so this page fell straight through to
   * "Contact not found" and told the user, indefinitely and with no spinner and
   * no error, that a contact who exists does not. There is no request in any log
   * on either side to contradict it; that is why this class survives
   * server-side investigation.
   *
   * The hook rather than the bare `readState()` because it carries the permanent
   * blocked-read diagnostic (CC-49899: a fail-soft path you cannot observe is
   * indistinguishable from a fix).
   */
  const contactQuery = useQuery({
    queryKey: ['contact', id],
    queryFn: () => contactService.getContact(id),
    enabled: !!id,
  })
  const { data: contact, error, refetch: refetchContact } = contactQuery
  const contactState = useReadState(`contact:${id}`, contactQuery)

  /*
   * `requireArray` in the queryFn, not `?.matters || []` at the render site.
   * `getMattersEnriched` returns `{ matters: resp.data, total: resp.meta.total }`,
   * so a 200 whose body carries no `data` hands this page `matters: undefined` on
   * a read that DID complete — the readShape.js family, and the sixth-instance
   * rule says do not write a seventh `|| []`. Rejecting the shape here means the
   * unreadable body lands in the same visible-refusal branch as a thrown read,
   * and it leaves a genuine `[]` completely alone. It also buys the render sites
   * an invariant they lean on: `ready` implies `data` is an array.
   */
  const mattersQuery = useQuery({
    queryKey: ['contactMatters', id],
    queryFn: async () => requireArray(
      (await matterService.getMattersEnriched({ limit: 100 }))?.matters,
      { noun: 'matter list', emptyNoun: 'set of matters', entryNoun: 'matter' }
    ),
    enabled: activeTab === 'Matters' && !!id,
  })
  const { data: allMatters, refetch: refetchMatters } = mattersQuery
  const mattersState = useReadState(`contact-matters:${id}`, mattersQuery)

  const updateMutation = useMutation({
    mutationFn: (data) => contactService.updateContact(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact', id] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      setEditing(false)
      toast.success('Contact updated')
    },
    onError: (err) => toast.error(err.message || 'Failed to update'),
  })

  const archiveMutation = useMutation({
    mutationFn: () => contactService.archiveContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      toast.success('Contact archived')
      navigate('/contacts')
    },
    onError: (err) => toast.error(err.message || 'Failed to archive'),
  })

  /*
   * All five states of the contact read, each with its own surface. There is no
   * fall-through: an unhandled state here renders NOTHING, i.e. a blank page,
   * which is how the first version of the catalog fix (spec 087) shipped a fifth
   * state nobody had looked at.
   *
   * `idle` gets its own branch rather than riding with `loading`, because this
   * query IS conditionally enabled (`enabled: !!id`). Folding it into loading
   * would hand a route with no `:id` a spinner that can never resolve, and
   * folding it into the error branch would accuse the server of losing a contact
   * nobody ever asked for. "Not asked yet" is the truth and it is what renders.
   */
  if (contactState === 'loading') {
    return <div className="p-12 flex justify-center"><LoadingSpinner message="Loading contact..." /></div>
  }

  if (contactState === 'idle') {
    return (
      <div className="max-w-4xl mx-auto">
        <Link to="/contacts" className="text-sm text-cb-accent hover:text-cb-navy mb-4 inline-block">&larr; Back to Contacts</Link>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-sm text-gray-500">No contact selected. Pick one from the contact list.</p>
        </div>
      </div>
    )
  }

  /* The state that used to render as "Contact not found". react-query PAUSED the
     fetch, so `queryFn` was never called: no request, no error, no spinner,
     nothing in any log. A spinner forever would be nearly as bad, so say what
     actually happened and offer the retry — `refetch` comes free with the query. */
  if (contactState === 'blocked') {
    return (
      <div className="max-w-4xl mx-auto">
        <Link to="/contacts" className="text-sm text-cb-accent hover:text-cb-navy mb-4 inline-block">&larr; Back to Contacts</Link>
        <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
          <p className="font-medium">Can&rsquo;t reach the server, so we haven&rsquo;t loaded this contact.</p>
          <p className="mt-1 text-amber-800">
            This does not mean the contact is missing or archived — the record was never read.
            Your connection looks offline; check it and try again.
          </p>
          <button
            type="button"
            onClick={() => refetchContact()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  /* A failed read is a different fact from a missing contact, and the user who
     sees the wrong one has no reason to retry — they conclude the record is gone.
     `role="alert"` so it is announced, and a retry so the recovery is not
     "reload the whole app". */
  if (contactState === 'failed') {
    return (
      <div className="max-w-4xl mx-auto">
        <Link to="/contacts" className="text-sm text-cb-accent hover:text-cb-navy mb-4 inline-block">&larr; Back to Contacts</Link>
        <div role="alert" className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-red-500">Failed to load contact: {error.message}</p>
          <p className="mt-1.5 text-sm text-gray-500">
            This does not mean the contact is missing or archived — nothing was read.
          </p>
          <button
            type="button"
            onClick={() => refetchContact()}
            className="mt-3 rounded-lg border border-rc-control bg-white px-3 py-1.5 text-xs font-medium text-cb-accent-text hover:bg-gray-50"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  // Read completed and CB genuinely has nothing under this id. Only NOW is
  // "not found" an observation rather than a guess, so it keeps its old copy.
  if (!contact) {
    return (
      <div className="max-w-4xl mx-auto">
        <Link to="/contacts" className="text-sm text-cb-accent hover:text-cb-navy mb-4 inline-block">&larr; Back to Contacts</Link>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-red-500">Contact not found</p>
        </div>
      </div>
    )
  }

  const name = contact.fullName || contact.name || '—'
  const isClient = contact.isClient === 'Yes' || contact.isClient === true

  const startEdit = () => {
    setEditData({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      company: contact.company || '',
    })
    setEditing(true)
  }

  const saveEdit = () => {
    updateMutation.mutate(editData)
  }

  // Filter matters that mention this contact (rough client-side filter since we don't have a direct link API)
  //
  // CC-53132: `undefined` survives on purpose. This used to start `(mattersData?.matters
  // || [])`, which turned every unresolved read into an empty list — and the heading
  // below counts it, so a read that never happened rendered "Related Matters (0)" over
  // "No related matters found". Optional chaining keeps the three cases apart:
  // `undefined` = the read has not resolved, `[]` = it resolved and this contact has no
  // matters. `requireArray` in the queryFn is what makes the invariant hold the other
  // way: state 'ready' implies `allMatters` is an array, so the branches below may
  // dereference `contactMatters` once they have ruled the other four states out.
  const contactMatters = allMatters?.filter((m) =>
    m.client?.toLowerCase().includes(name.toLowerCase()) ||
    m.name?.toLowerCase().includes(name.toLowerCase())
  )

  return (
    <div className="max-w-4xl mx-auto">
      <Link to="/contacts" className="text-sm text-cb-accent hover:text-cb-navy mb-4 inline-block">&larr; Back to Contacts</Link>

      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="px-6 py-5 flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-cb-purple flex items-center justify-center text-white text-xl font-semibold">
              {name[0]?.toUpperCase() || 'C'}
            </div>
            <div>
              <h1 className="text-xl font-display text-gray-900">{name}</h1>
              <div className="flex items-center gap-3 mt-1">
                {isClient && (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">Client</span>
                )}
                {contact.contactType && (
                  <span className="text-sm text-gray-500">{contact.contactType}</span>
                )}
                {contact.status && contact.status !== 'Active' && (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{contact.status}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={startEdit}
              className="px-3 py-1.5 text-sm font-medium text-cb-accent border border-cb-accent/30 rounded-lg hover:bg-cb-light transition-colors"
            >
              Edit
            </button>
            <button
              onClick={() => { if (confirm('Archive this contact?')) archiveMutation.mutate() }}
              className="px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
            >
              Archive
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit" role="tablist" aria-label="Contact detail tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'Overview' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">Contact Information</h2>
          </div>
          <div className="p-6">
            {editing ? (
              <div className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    aria-label="Contact name"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cb-accent/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    aria-label="Contact email"
                    value={editData.email}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cb-accent/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    aria-label="Contact phone"
                    value={editData.phone}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cb-accent/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    aria-label="Contact company"
                    value={editData.company}
                    onChange={(e) => setEditData({ ...editData, company: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cb-accent/30"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={saveEdit}
                    disabled={updateMutation.isPending}
                    className="px-4 py-2 bg-cb-accent text-white rounded-lg text-sm font-medium hover:bg-cb-accent/90 disabled:opacity-50"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditing(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <dl>
                <DetailRow label="Name" value={name} />
                <DetailRow label="Email" value={contact.email} />
                <DetailRow label="Phone" value={contact.phone} />
                <DetailRow label="Company" value={contact.company} />
                <DetailRow label="Type" value={contact.contactType} />
                <DetailRow label="Client" value={contact.isClient} />
                <DetailRow label="Status" value={contact.status || 'Active'} />
              </dl>
            )}
          </div>
        </div>
      )}

      {activeTab === 'Matters' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            {/* The count rides on a COMPLETED read. A "(0)" beside an unfinished
                read is a claim, not an observation — and this one sat in a heading
                the user reads before they reach the body, so it made the empty
                state below look corroborated. When the read has not resolved the
                parenthetical is simply not rendered; the body says why. */}
            <h2 className="text-base font-semibold text-gray-900">
              Related Matters{contactMatters ? ` (${contactMatters.length})` : ''}
            </h2>
          </div>
          {mattersState === 'blocked' ? (
            /* Paused: no request was sent, so nothing below this line is known. */
            <div role="alert" className="m-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-medium">Can&rsquo;t reach the server, so we haven&rsquo;t loaded this contact&rsquo;s matters.</p>
              <p className="mt-1 text-amber-800">
                This is not an empty list — the matter list was never read. Your connection
                looks offline; check it and try again.
              </p>
              <button
                type="button"
                onClick={() => refetchMatters()}
                className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
              >
                Try again
              </button>
            </div>
          ) : mattersState === 'failed' ? (
            /* Failed. This read had NO error surface at all before: the query's
               `error` was never destructured, so a thrown read rendered exactly
               like a contact with no matters. */
            <div role="alert" className="m-6 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-700">
              <p className="font-medium">We couldn&rsquo;t load this contact&rsquo;s matters: {mattersQuery.error?.message}</p>
              <p className="mt-1">
                This is not an empty list — nothing was read, so no matter is missing.
              </p>
              <button
                type="button"
                onClick={() => refetchMatters()}
                className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
              >
                Try again
              </button>
            </div>
          ) : mattersState === 'loading' || mattersState === 'idle' ? (
            /* `idle` rides with `loading` HERE, and only because of where "here"
               is. The query is `enabled: activeTab === 'Matters' && !!id`, and
               this panel renders only when `activeTab === 'Matters'`, with `!id`
               already turned away by the contact read's own idle branch above. So
               inside this panel the query is always enabled, and `idle` can only
               be the tick between enabling and the fetch actually starting — a
               loading state, not a "not asked yet" one, and it cannot strand the
               panel on a permanent spinner. If the `enabled` condition ever stops
               matching this render gate, split them again: `idle` becomes a real
               "not asked yet" and a spinner would then never resolve. */
            <div className="p-8 flex justify-center"><LoadingSpinner message="Loading matters..." /></div>
          ) : contactMatters.length === 0 ? (
            <p className="p-8 text-sm text-gray-500 text-center">No related matters found</p>
          ) : (
            <table className="w-full text-sm" role="table">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th scope="col" className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Matter</th>
                  <th scope="col" className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Number</th>
                  <th scope="col" className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Attorney</th>
                  <th scope="col" className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {contactMatters.map((m) => (
                  <tr
                    key={m.id || m.matterId}
                    onClick={() => navigate(`/matters/${m.id || m.matterId}`)}
                    className="hover:bg-gray-50/50 cursor-pointer"
                  >
                    <td className="px-5 py-3 text-cb-accent font-medium">{m.name}</td>
                    <td className="px-5 py-3 text-gray-600">{m.number || '—'}</td>
                    <td className="px-5 py-3 text-gray-600">{m.attorney || '—'}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        m.status === 'Inactive' ? 'bg-gray-100 text-gray-600' : 'bg-green-50 text-green-700'
                      }`}>
                        {m.status || 'Active'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'Activities' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
          <p className="text-sm text-gray-500">Activity history coming soon</p>
        </div>
      )}
    </div>
  )
}

export default ContactDetail
